<template>
  <div class="product-listing">
    <template v-if="products.length">
      <FilterList
        class="product-listing__filters"
        :sort-options="sortOptions"
      />

      <PaginatorToolbar
        class="product-listing__paginator product-listing__paginator--top"
        :sort="sort"
        :sort-options="sortOptions"
        :total-count="totalCount"
        v-on="paginatorMethods"
      />

      <div
        class="product-listing__grid products"
        :class="`products--${view}`"
      >
        <ProductTile
          v-for="(item, index) in products"
          :key="item.sku"
          :product="item"
          :lazy="index > 2"
          list="PDPRelatedListing"
        />
      </div>

      <PaginatorToolbar
        class="product-listing__paginator product-listing__paginator--bottom"
        :current-page="page"
        :total-pages="totalPages"
        v-on="paginatorMethods"
      />
    </template>
    <p
      v-else
      class="product-listing__empty"
    >
      {{ $t('No products found') }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import esUrlQuery from '~layers/app/graphql/routes/esUrlQuery.gql'
import type { EsProduct } from '~layers/app/types/elastic'

const props = defineProps<{
  product: EsProduct | null | undefined
}>()

const { client } = useApolloClient('default')

// Sorted categories deepest first (leaf → root)
const sortedCategories = computed(() => {
  const cats = (props.product?.categories ?? []) as Array<{
    id: string
    level: number
    name: string
    parentId: string
    path: string
    url: string
  }>
  return [...cats].sort((a, b) => b.level - a.level)
})

// Fetch category filters from the ES URL index — same mechanism as PLP pages.
const categoryFilters = ref<any[]>([])

const fetchCategoryFilters = async () => {
  for (const cat of sortedCategories.value) {
    if (!cat.url) continue
    const esPath = cat.url.replace(/^\/+|\/+$/g, '')
    const result = await client.query({ query: esUrlQuery, variables: { url: esPath } })
    const urlFilters = result?.data?.esUrl?.url?.filters
    if (urlFilters?.length) {
      categoryFilters.value = urlFilters
      return
    }
  }
}

await fetchCategoryFilters()

// Brand filter — use Tweakwise's tra_brand key (case-sensitive, e.g. "Eastpak")
const BRAND_FILTER_KEY = 'tra_brand'

const brandLabel = computed(() => {
  const brand = (props.product as any)?.brand
  return (typeof brand === 'object' ? brand?.label : brand) || null
})

const listingMeta = computed(() => ({
  filters: [
    ...categoryFilters.value,
    ...(brandLabel.value
      ? [{ key: BRAND_FILTER_KEY, values: [brandLabel.value] }]
      : []),
  ],
  lockedFilterKeys: [
    ...categoryFilters.value.map((f: any) => f.key),
    BRAND_FILTER_KEY,
  ],
  defaultSort: 'Best verkocht (builder)',
}))

provide('meta', listingMeta)

const { products, sortOptions, fetchProductData } = useCatalog(listingMeta)
useCatalogFilters(listingMeta)

const {
  page,
  totalPages,
  sort,
  view,
  totalCount,
  paginatorMethods,
} = useCatalogPaginator()

const { closePanel } = useCatalogFiltersModal()

onMounted(() => {
  closePanel()
})

await fetchProductData()
</script>
